/**
 * Feb 2026 — Partner Portal mirror of the Admin "Partner | Link | Region |
 * Total | Revenue | Commission" combined report.
 *
 * Calls `GET /api/partner/dashboard/attribution-summary`, which reuses the
 * EXACT SAME `pws_reporting_service.partner_combined_summary()` aggregation
 * the Admin report calls — scoped server-side to the logged-in partner's
 * own `partner_id` (never trusts a client-supplied id). Admin and Partner
 * Portal therefore can never drift into different bucket definitions.
 *
 * Buckets (identical wording/definitions to the Admin report):
 *   Link Bookings            = partner_link + stored_partner_attribution
 *   Region Direct             = region_allocation
 *   Total Territory Bookings  = Link Bookings + Region Direct
 * Commission is read verbatim from the commission ledger — never
 * recalculated from revenue, and commission eligibility is unchanged
 * (only partner_link bookings are commission-eligible).
 */
import { useEffect, useMemo, useState } from "react";
import { Link2, MapPin, Wallet, DollarSign, Loader2, ChevronDown, ChevronUp, Info } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

const WINDOWS = [
  { id: "all",        label: "All time" },
  { id: "this_month", label: "This month" },
  { id: "last_30d",   label: "Last 30d" },
  { id: "last_7d",    label: "Last 7d" },
];

const fmtMoney = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtNum = (n) => Number(n || 0).toLocaleString();

const EMPTY_ROW = {
  link_bookings: 0,
  link_bookings_breakdown: { partner_link: 0, stored_partner_attribution: 0 },
  region_direct: 0,
  region_direct_confidence: { region_match: 0, region_match_review_required: 0, nearest_partner_fallback: 0 },
  total_territory_bookings: 0,
  revenue: 0,
  revenue_breakdown: { partner_link: 0, stored_partner_attribution: 0, region_allocation: 0 },
  commission: 0,
  commission_breakdown: {},
};

function StatTile({ icon: Icon, label, value, tone, testId, sub }) {
  return (
    <div data-testid={testId} className={`rounded-xl border border-slate-200 bg-white p-4 flex items-start gap-3 min-w-0`}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${tone}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{label}</div>
        <div className="text-xl sm:text-2xl font-bold text-slate-900 mt-0.5 tabular-nums break-all">{value}</div>
        {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

export default function PartnerAttributionSummary() {
  const [win, setWin] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [row, setRow] = useState(EMPTY_ROW);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expanded, setExpanded] = useState(false);
  const usingCustomRange = Boolean(startDate && endDate);
  const rangeInvalid = usingCustomRange && startDate > endDate;

  // Collapse the fetch trigger to a single stable key so typing a date
  // (which updates startDate/endDate/usingCustomRange/rangeInvalid all
  // at once) only causes ONE network request, not several.
  const paramsKey = useMemo(
    () => (rangeInvalid ? null : usingCustomRange ? `range:${startDate}:${endDate}` : `window:${win}`),
    [win, startDate, endDate, usingCustomRange, rangeInvalid],
  );

  useEffect(() => {
    if (paramsKey === null) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    setError(null);
    const params = usingCustomRange ? { start_date: startDate, end_date: endDate } : { window: win };
    api.get("/partner/dashboard/attribution-summary", { params })
      .then(({ data }) => { if (alive) setRow(data.row || EMPTY_ROW); })
      .catch((e) => { if (alive) setError(formatApiError(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramsKey]);

  const hasReview = (row.region_direct_confidence?.region_match_review_required || 0) > 0;

  return (
    <div
      data-testid="partner-attribution-summary"
      className="mt-6 rounded-2xl bg-white border border-slate-200 p-5"
    >
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-1">
        <div>
          <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700">
            Booking Attribution &amp; Commission
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div
            className="inline-flex rounded-full bg-slate-100 p-1 text-[11px] font-semibold"
            data-testid="partner-attribution-window-selector"
          >
            {WINDOWS.map((w) => (
              <button
                key={w.id}
                onClick={() => { setStartDate(""); setEndDate(""); setWin(w.id); }}
                data-testid={`partner-attribution-window-${w.id}`}
                className={`px-2.5 py-1 rounded-full transition ${
                  win === w.id && !usingCustomRange
                    ? "bg-cyan-600 text-white shadow-sm"
                    : "text-slate-600 hover:text-slate-800"
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1" data-testid="partner-attribution-date-range">
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              data-testid="partner-attribution-start-date"
              className="text-[11px] border border-slate-200 rounded-md px-1.5 py-1 w-[128px]"
            />
            <span className="text-[10px] text-slate-400">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              data-testid="partner-attribution-end-date"
              className="text-[11px] border border-slate-200 rounded-md px-1.5 py-1 w-[128px]"
            />
            {usingCustomRange && (
              <button
                onClick={() => { setStartDate(""); setEndDate(""); }}
                data-testid="partner-attribution-clear-range"
                className="text-[10px] font-semibold text-cyan-700 hover:underline px-1"
              >
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      <div
        className="mt-2 mb-4 rounded-lg bg-slate-50 border border-slate-100 px-3 py-2.5 text-[11px] text-slate-600 flex items-start gap-2"
        data-testid="partner-attribution-region-direct-explainer"
      >
        <Info className="w-3.5 h-3.5 text-slate-400 flex-shrink-0 mt-0.5" />
        <span>
          <strong>Region Direct</strong> means an AWARE booking that came directly to AWARE, originating
          within your assigned service territory — it does <strong>not</strong> mean you directly
          referred that customer, and it does <strong>not</strong> automatically mean commission is
          payable on it. <strong>Link Bookings</strong> are bookings attributed to your referral link
          (fresh click or the 90-day stored attribution window).
        </span>
      </div>

      {rangeInvalid ? (
        <div data-testid="partner-attribution-range-invalid" className="text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-lg py-2 px-3">
          Start date must be on or before end date.
        </div>
      ) : loading ? (
        <div data-testid="partner-attribution-loading" className="text-xs text-slate-400 italic py-4 flex items-center gap-2">
          <Loader2 className="w-3.5 h-3.5 animate-spin" /> Loading…
        </div>
      ) : error ? (
        <div data-testid="partner-attribution-error" className="text-xs text-rose-700 py-2">{error}</div>
      ) : (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            <StatTile
              icon={Link2} label="Link Bookings" value={fmtNum(row.link_bookings)}
              tone="bg-emerald-100 text-emerald-800" testId="partner-attribution-link-bookings"
            />
            <StatTile
              icon={MapPin} label="Region Direct" value={fmtNum(row.region_direct)}
              tone="bg-indigo-100 text-indigo-800" testId="partner-attribution-region-direct"
              sub={hasReview ? `${fmtNum(row.region_direct_confidence.region_match_review_required)} pending review` : null}
            />
            <StatTile
              icon={MapPin} label="Total Territory Bookings" value={fmtNum(row.total_territory_bookings)}
              tone="bg-slate-200 text-slate-700" testId="partner-attribution-total-territory"
            />
            <StatTile
              icon={DollarSign} label="Revenue" value={fmtMoney(row.revenue)}
              tone="bg-cyan-100 text-cyan-800" testId="partner-attribution-revenue"
            />
            <StatTile
              icon={Wallet} label="Commission" value={fmtMoney(row.commission)}
              tone="bg-amber-100 text-amber-800" testId="partner-attribution-commission"
            />
          </div>

          <button
            onClick={() => setExpanded((v) => !v)}
            data-testid="partner-attribution-expand-toggle"
            className="mt-3 inline-flex items-center gap-1 text-[11px] font-semibold text-cyan-700 hover:underline"
          >
            {expanded ? "Hide breakdown" : "Show breakdown"}
            {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          </button>

          {expanded && (
            <div
              data-testid="partner-attribution-breakdown"
              className="mt-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 rounded-lg bg-slate-50/70 border border-slate-100 p-4 text-[12px]"
            >
              <div>
                <div className="font-bold text-slate-600 uppercase tracking-wide text-[9px] mb-1.5">Link bookings</div>
                <div>Partner link (direct): <strong>{fmtNum(row.link_bookings_breakdown?.partner_link)}</strong></div>
                <div>Stored (90-day): <strong>{fmtNum(row.link_bookings_breakdown?.stored_partner_attribution)}</strong></div>
              </div>
              <div>
                <div className="font-bold text-slate-600 uppercase tracking-wide text-[9px] mb-1.5">Region confidence</div>
                <div>Confident match: <strong>{fmtNum(row.region_direct_confidence?.region_match)}</strong></div>
                <div className={hasReview ? "text-amber-700" : ""}>
                  Review required: <strong>{fmtNum(row.region_direct_confidence?.region_match_review_required)}</strong>
                </div>
                <div>Nearest-territory fallback: <strong>{fmtNum(row.region_direct_confidence?.nearest_partner_fallback)}</strong></div>
              </div>
              <div>
                <div className="font-bold text-slate-600 uppercase tracking-wide text-[9px] mb-1.5">Revenue by source</div>
                <div>Partner link: <strong>{fmtMoney(row.revenue_breakdown?.partner_link)}</strong></div>
                <div>Stored (90-day): <strong>{fmtMoney(row.revenue_breakdown?.stored_partner_attribution)}</strong></div>
                <div>Region direct: <strong>{fmtMoney(row.revenue_breakdown?.region_allocation)}</strong></div>
              </div>
              <div>
                <div className="font-bold text-slate-600 uppercase tracking-wide text-[9px] mb-1.5">Commission status</div>
                {Object.keys(row.commission_breakdown || {}).length === 0 ? (
                  <div className="text-slate-400 italic">No ledger entries in this window.</div>
                ) : (
                  Object.entries(row.commission_breakdown).map(([k, v]) => (
                    <div key={k} className="capitalize">{k}: <strong>{fmtMoney(v)}</strong></div>
                  ))
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
